import { useRef, useState } from "react";
import { playFlipSound } from "../utils/audio";

interface Panel {
  x: string;
  y: string;
  className: string;
  text: string;
}

const pages: { title: string; panels: Panel[] }[] = [
  {
    title: "第 1 页 · 翻开",
    panels: [
      { x: "12%", y: "18%", className: "bg-primary-100", text: "清晨，阳光洒进窗台……" },
      { x: "58%", y: "12%", className: "bg-accent-100", text: "主角翻开第一页漫画。" },
      { x: "30%", y: "58%", className: "bg-secondary-100", text: "指尖轻触，纸页沙沙作响。" },
    ],
  },
  {
    title: "第 2 页 · 音效",
    panels: [
      { x: "14%", y: "16%", className: "bg-accent-100", text: "清脆的翻页声响起。" },
      { x: "56%", y: "44%", className: "bg-primary-100", text: "声音跟随翻动节奏起伏。" },
      { x: "22%", y: "66%", className: "bg-secondary-100", text: "沉浸感扑面而来。" },
    ],
  },
  {
    title: "第 3 页 · 动画",
    panels: [
      { x: "48%", y: "18%", className: "bg-secondary-100", text: "画面随手指流畅翻动。" },
      { x: "12%", y: "40%", className: "bg-primary-100", text: "高帧率动画不拖影。" },
      { x: "60%", y: "62%", className: "bg-accent-100", text: "纸感阅读由此开始。" },
    ],
  },
];

function FlipAnimation() {
  const [index, setIndex] = useState(0);
  const [flipping, setFlipping] = useState(false);
  const timeoutRef = useRef<number | null>(null);

  const total = pages.length;

  const turn = (dir: 1 | -1) => {
    if (flipping) return;
    setFlipping(true);
    playFlipSound(dir === 1 ? "crisp" : "soft", 0.8);
    if (timeoutRef.current) window.clearTimeout(timeoutRef.current);
    timeoutRef.current = window.setTimeout(() => {
      setIndex((i) => (i + dir + total) % total);
      setFlipping(false);
    }, 260);
  };

  const page = pages[index];
  const next = pages[(index + 1) % total];

  return (
    <section id="flip" className="bg-background-50 py-14 md:py-20">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2 lg:gap-14">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-800">
              <i className="ri-stack-line"></i>
              翻页动画演示
            </span>
            <h2 className="mt-3 font-heading text-3xl text-foreground-950 md:text-4xl">
              指尖轻滑，纸页翻飞
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">
              右侧是哔咔漫画的翻页动画演示，点击「下一页 / 上一页」即可体验带音效的 3D 纸感翻页。
              动画采用 GPU 加速与逐帧渲染，过渡自然顺滑，翻动幅度与方向均可自由调整。
            </p>

            <div className="mt-6 space-y-3">
              {[
                { icon: "ri-pulse-line", t: "流畅 60 帧", d: "翻页全程无卡顿、不拖影。" },
                { icon: "ri-equalizer-line", t: "可调翻页曲线", d: "弹性、匀速、平顺多种手感。" },
                { icon: "ri-volume-up-line", t: "声画同步", d: "翻页动画与音效完美契合。" },
              ].map((f) => (
                <div key={f.t} className="flex items-start gap-3 rounded-xl border border-background-200 bg-background-50 p-4">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-accent-100 text-accent-800">
                    <i className={`${f.icon} text-lg`}></i>
                  </span>
                  <span>
                    <span className="block font-medium text-foreground-950">{f.t}</span>
                    <span className="mt-0.5 block text-sm text-foreground-600">{f.d}</span>
                  </span>
                </div>
              ))}
            </div>

            <div className="mt-6 flex items-center gap-3">
              <button
                type="button"
                onClick={() => turn(-1)}
                className="flex items-center gap-2 whitespace-nowrap rounded-full border border-background-200 bg-background-100 px-5 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:bg-secondary-100"
              >
                <i className="ri-arrow-left-line"></i>
                上一页
              </button>
              <button
                type="button"
                onClick={() => turn(1)}
                className="flex items-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
              >
                下一页
                <i className="ri-arrow-right-line"></i>
              </button>
            </div>
          </div>

          <div className="flex justify-center">
            <div className="relative aspect-[3/4] w-full max-w-sm [perspective:1600px]">
              {/* 下一页垫底 */}
              <div className="absolute inset-0 rounded-2xl border border-background-200 bg-background-100 p-5 shadow-inner">
                <span className="text-xs text-foreground-500">{next.title}</span>
              </div>

              {/* 当前页 */}
              <div
                key={index}
                className={`absolute inset-0 rounded-2xl border border-background-200 bg-background-50 p-5 [transform-style:preserve-3d] ${flipping ? "flip-anim" : "flip-in"}`}
              >
                <div className="flex items-center justify-between text-xs text-foreground-500">
                  <span>哔咔漫画</span>
                  <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary-100 text-primary-700">
                    <i className="ri-book-open-line"></i>
                  </span>
                </div>
                <h3 className="mt-2 font-medium text-foreground-950">{page.title}</h3>
                <div className="relative mt-3 flex-1">
                  {page.panels.map((p, i) => (
                    <div
                      key={i}
                      className={`absolute flex h-24 w-40 items-center justify-center rounded-lg p-2 text-center text-xs leading-relaxed text-foreground-700 ${p.className}`}
                      style={{ left: p.x, top: p.y }}
                    >
                      {p.text}
                    </div>
                  ))}
                </div>
                <p className="absolute bottom-4 left-5 text-xs text-foreground-400">
                  {index + 1} / {total}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default FlipAnimation;